import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { VideoEmbedModal } from '@/components/ui/VideoEmbedModal';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';
import { getHelpContent } from '@/services/help.service';
import { extractVimeoHash, fetchOembedThumbnail } from '@/utils/oembed';
import type { HelpVideo, HelpVideoProvider, HelpVideoType } from '@/types/help';

/**
 * Help Page
 *
 * Help center with support resources, video tutorials, and contact options.
 * Dynamic video sections are loaded from the Help API.
 */

// ─── Helper: resolve thumbnail URL ──────────────────────────────────────────

function resolveThumbnail(
  thumbnailUrl: string | null,
  provider: HelpVideoProvider,
  videoId: string,
  type: HelpVideoType
): string | null {
  if (thumbnailUrl) return thumbnailUrl;
  if (provider === 'YOUTUBE' && type === 'VIDEO') {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  // Vimeo videos without an admin-uploaded thumbnail: no synchronous source.
  // vumbnail.com is intentionally skipped — it returns HTTP 200 with a fake
  // "error.jpg" placeholder when the lookup fails, so onError never fires.
  // The oEmbed query in VideoCard handles Vimeo thumbnails reliably instead.
  return null;
}

// ─── Helper: caption text ────────────────────────────────────────────────────

function videoCaption(provider: HelpVideoProvider, type: HelpVideoType): string {
  if (provider === 'YOUTUBE' && type === 'PLAYLIST') return 'YouTube Playlist';
  if (provider === 'VIMEO' && type === 'PLAYLIST') return 'Vimeo Showcase';
  if (provider === 'VIMEO') return 'Watch (Vimeo)';
  return 'Watch (YouTube)';
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface ExternalLinkCardProps {
  title: string;
  description: string;
  icon: string;
  gradientFrom: string;
  gradientTo: string;
  href?: string;
  buttonText: string;
  onClick?: () => void;
}

const ExternalLinkCard = ({
  title,
  description,
  icon,
  gradientFrom,
  gradientTo,
  href,
  buttonText,
  onClick,
}: ExternalLinkCardProps) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div
        className={cn(
          'absolute inset-x-0 top-0 h-1 bg-gradient-to-r',
          `from-${gradientFrom}-500 to-${gradientTo}-500`
        )}
      />
      <div className="p-5 sm:p-8 text-center">
        {/* Icon — tinted bg, no pulse */}
        <div className="relative mx-auto mb-4 h-16 w-16">
          <div
            className={cn(
              'absolute inset-0 rounded-2xl opacity-20',
              `bg-${gradientFrom}-100`
            )}
          />
          <div
            className={cn(
              'relative h-full w-full rounded-2xl flex items-center justify-center',
              `bg-${gradientFrom}-500`
            )}
          >
            <Icon name={icon} size="lg" color="text-white" ariaHidden />
          </div>
        </div>

        <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{description}</p>

        <button
          onClick={handleClick}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary-400 hover:bg-primary-600 text-primary-400-contrast font-semibold transition-all duration-200 hover:shadow-lg"
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

// ─── VideoCard ────────────────────────────────────────────────────────────────

interface VideoCardProps {
  video: HelpVideo;
}

const VideoCard = ({ video }: VideoCardProps) => {
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  const caption = videoCaption(video.provider, video.type);
  const vimeoHash = extractVimeoHash(video.videoUrl);

  // Synchronous source: admin upload OR YouTube CDN. Null for Vimeo (filled by oEmbed below).
  const syncThumb = resolveThumbnail(video.thumbnailUrl, video.provider, video.videoId, video.type);

  // Videos without a manual thumbnail need an oEmbed lookup:
  //  - All Vimeo content (single videos + showcases): oEmbed is the only reliable path.
  //    vumbnail.com is skipped — it returns HTTP 200 with a fake "error.jpg" placeholder
  //    on lookup failures, so onError never fires and broken images are shown silently.
  //  - YouTube playlists: no static CDN thumbnail; oEmbed returns the first video's hqdefault.
  //  - YouTube single videos: static CDN URL is used (syncThumb), no oEmbed needed.
  const needsOEmbed =
    !video.thumbnailUrl &&
    (
      (video.provider === 'VIMEO') ||
      (video.provider === 'YOUTUBE' && video.type === 'PLAYLIST')
    );

  const { data: oembedThumb } = useQuery<string | null>({
    queryKey: ['oembed', video.provider, video.type, video.videoId, vimeoHash],
    queryFn: () => fetchOembedThumbnail({
      provider: video.provider,
      type: video.type,
      videoId: video.videoId,
      vimeoHash,
    }),
    enabled: needsOEmbed,
    staleTime: 24 * 60 * 60 * 1000, // 1 day — thumbnails don't change often
    retry: 1,
  });

  // Effective image: admin/YouTube sync source first, then oEmbed result for Vimeo.
  // While needsOEmbed && !oembedThumb (query in flight), effectiveSrc is null and
  // the branded gradient placeholder shows — oEmbed is fast and cached for 24h.
  const effectiveSrc = syncThumb ?? oembedThumb ?? null;
  const showImg = effectiveSrc !== null && !imgError;

  return (
    <>
      <Tooltip
        content={
          <div className="max-w-xs">
            <p className="font-semibold text-sm">{video.title}</p>
            {video.description && (
              <p className="text-xs text-gray-300 mt-1 whitespace-normal">{video.description}</p>
            )}
          </div>
        }
        side="top"
        delayDuration={400}
        disabled={!video.description}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-200 text-left w-full"
          aria-label={`Play video: ${video.title}`}
        >
          {/* Thumbnail / placeholder */}
          <div className="relative aspect-video bg-gray-100 overflow-hidden">
            {showImg ? (
              <img
                src={effectiveSrc}
                alt={video.title}
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                onError={() => setImgError(true)}
              />
            ) : (
              /* Polished branded placeholder — shown when all thumbnail sources fail */
              <div className="absolute inset-0 bg-gradient-to-br from-primary-400 via-primary-500 to-primary-600 flex flex-col items-center justify-center p-6 text-center">
                <Icon name={ICONS.VIDEO} size="lg" color="text-white/80" ariaHidden />
                <h5 className="mt-2 text-base font-bold text-white line-clamp-2">{video.title}</h5>
                <span className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 text-xs font-semibold text-white uppercase tracking-wide">
                  {video.provider === 'YOUTUBE' ? 'YouTube' : 'Vimeo'}
                </span>
              </div>
            )}

            {/* Play overlay — always on top, over both thumbnail and placeholder */}
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <div className="h-16 w-16 rounded-full bg-white/95 group-hover:bg-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-all">
                {/* Inline SVG avoids font-metric centering issues with HappyIcons play glyph.
                    translate-x-[1px] is an optical nudge: the eye reads the triangle tip as center. */}
                <svg
                  viewBox="0 0 24 24"
                  className="h-9 w-9 text-primary-500 translate-x-[1px]"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Title + caption */}
          <div className="p-4">
            <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
              {video.title}
            </h4>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <p className="text-xs text-gray-500 mt-1">{caption}</p>
          </div>
        </button>
      </Tooltip>

      <VideoEmbedModal
        isOpen={open}
        onClose={() => setOpen(false)}
        provider={video.provider}
        type={video.type}
        videoId={video.videoId}
        videoUrl={video.videoUrl}
        title={video.title}
        description={video.description}
      />
    </>
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const VideoSectionSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-6 w-48 bg-gray-200 rounded" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
      {[1, 2].map((i) => (
        <div key={i} className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="aspect-video bg-gray-200" />
          <div className="p-4 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-100 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export const Help = () => {
  const { t } = useTranslation('common');

  const { data: sections, isLoading, isError, refetch } = useQuery({
    queryKey: ['help', 'sections'],
    queryFn: getHelpContent,
    staleTime: 5 * 60 * 1000,
  });

  const openCrispChat = () => {
    const crisp = (window as Window & { $crisp?: unknown[] }).$crisp;
    if (crisp) {
      crisp.push(['do', 'chat:open']);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={t('help_title') || 'Help Center'}
        subtitle={t('help_subtitle') || 'Get support and resources to help you succeed'}
        icon={ICONS.INFO}
        headerType="list"
        documentTitle="Help"
      />

      {/* Quick Links Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ExternalLinkCard
          title={t('help_video_tutorials') || 'Video Tutorials'}
          description={t('help_video_desc') || 'Watch step-by-step guides on YouTube'}
          icon={ICONS.PLAY}
          gradientFrom="red"
          gradientTo="pink"
          href="https://www.youtube.com/@aegile-aessets/videos"
          buttonText={t('help_view_channel') || 'View Channel'}
        />

        <ExternalLinkCard
          title={t('help_knowledge_base') || 'Knowledge Base'}
          description={t('help_kb_desc') || 'Browse articles and FAQs in our help center'}
          icon={ICONS.FILE}
          gradientFrom="blue"
          gradientTo="cyan"
          href="https://agile-assets.crisp.help/en/"
          buttonText={t('help_browse_faqs') || 'Browse FAQs'}
        />

        <ExternalLinkCard
          title={t('help_live_chat') || 'Live Chat'}
          description={t('help_chat_desc') || 'Talk to our team • Mon-Fri 9-18 CET'}
          icon={ICONS.CHAT}
          gradientFrom="green"
          gradientTo="emerald"
          buttonText={t('help_open_chat') || 'Open Chat'}
          onClick={openCrispChat}
        />
      </div>

      {/* Dynamic Video Sections */}
      {isLoading && (
        <div className="space-y-6">
          <VideoSectionSkeleton />
          <VideoSectionSkeleton />
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 flex items-center gap-4">
          <Icon name={ICONS.WARNING} size="md" color="text-red-500" ariaHidden />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-700">
              {t('help_load_error') || 'Could not load video tutorials.'}
            </p>
          </div>
          <button
            onClick={() => void refetch()}
            className="text-sm font-semibold text-red-600 hover:text-red-800 transition-colors"
          >
            {t('retry') || 'Try again'}
          </button>
        </div>
      )}

      {sections && sections.length > 0 && (
        <div className="space-y-6">
          {sections
            .filter((section) => section.videos.length > 0)
            .map((section) => (
              <div key={section.id} className="space-y-4">
                <h2 className="text-base sm:text-xl font-bold text-gray-900">{section.title}</h2>
                {section.description && (
                  <p className="text-sm text-gray-600">{section.description}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {section.videos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* VIP Founder Call Section */}
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-gradient-coral shadow-sm">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary-500 to-secondary-500" />
        <div className="px-6 py-6 sm:p-8">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Kristin's Photo */}
            <div className="flex-shrink-0 w-full md:w-48 text-center">
              <img
                src="/images/kristin.jpeg"
                alt="Kristin Reinbach"
                className="h-32 w-32 rounded-full object-cover border-4 border-white shadow-lg mx-auto"
              />
              <div className="mt-3">
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <p className="font-bold text-gray-900">Kristin Reinbach</p>
                <p className="text-sm text-gray-600">
                  {t('help_founder_title') || 'Founder of Ägile Ässets'}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t('help_vip_heading') || 'You are special'} ✨
              </h3>
              <p className="text-gray-700 mb-4">
                {t('help_vip_question') ||
                  'Have you already made use of your VIP call with our founder Kristin?'}
              </p>

              <div className="space-y-3 mb-5 text-sm text-gray-700">
                <p>
                  {t('help_founder_desc_1') ||
                    'Hi there! I am Kristin and together with our team at OVERW8 Digital Brand Agency we have built and stress tested the content management tool we would often have wished for.'}
                </p>
                <p>
                  {t('help_founder_desc_2') ||
                    'Ägile Ässets has been built in a "scratch your own itch" mode. We have been working with it ourselves for nearly 2 years now and are happy to share it with you. Hope you like it as much as we do!'}
                </p>
                <p>
                  {t('help_founder_desc_3') ||
                    'We have been developing and adding new features quickly - some stuff might not be covered in online resources yet. Please bear with us and always feel free to reach out via the chat.'}
                </p>
                <p className="font-medium">
                  {t('help_founder_desc_4') ||
                    'Most of all: I will always be happy to give you a VIP intro - feel free to book a slot here:'}
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <a
                  href="https://calendly.com/o8-digitalagency/kaffee-mit-kristin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-400 hover:bg-primary-600 text-primary-400-contrast font-semibold rounded-xl hover:shadow-lg transition-all duration-200"
                >
                  <Icon name={ICONS.CALENDAR} size="sm" color="text-primary-400-contrast" ariaHidden />
                  {t('help_book_calendly') || 'Book a Slot Now'}
                </a>
                <a
                  href="https://www.linkedin.com/in/kristin-reinbach"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-xl hover:shadow-md transition-all duration-200"
                >
                  <FontAwesomeIcon icon={faLinkedin} className="text-base text-[#0A66C2]" />
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <>LinkedIn</>
                </a>
              </div>

              <p className="mt-4 text-sm text-gray-600 italic">
                {t('help_farewell') || "Here's to an ägile day - greets from Munich!"} 👋
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Link */}
      <div className="text-center pt-4">
        <a
          href="/status"
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-primary-600 transition-colors"
        >
          {t('help_status_link') || 'System Status & Feature Requests'}
          <Icon name={ICONS.ARROW_RIGHT} size="xs" ariaHidden />
        </a>
      </div>
    </div>
  );
};
